import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom'
import { useCallback, useEffect, useRef, useState, Suspense } from 'react'
import { usePortalAuth } from '../context/PortalAuthContext'
import portalApi from '../services/portalApi'
import ChunkLoadErrorBoundary, { ResetChunkReloadFlag } from './ChunkLoadErrorBoundary'
import PageLoading from './PageLoading'
import useIdlePrefetch from '../hooks/useIdlePrefetch'
import { useViewportTier, useOverlayMenu } from '../hooks/useShellLayout'

const NAV = [
  { to: '/portal/dashboard', label: 'Dashboard', icon: '🏠' },
  { to: '/portal/invoices',  label: 'Invoices',  icon: '💰' },
  { to: '/portal/trust',     label: 'Trust',     icon: '🏦' },
  { to: '/portal/matters',   label: 'Matters',   icon: '📁' },
  { to: '/portal/documents', label: 'Documents', icon: '📂' },
  { to: '/portal/events',    label: 'Events',    icon: '📅' },
  { to: '/portal/profile',   label: 'Profile',   icon: '👤' }
]

// The client portal's shell (docs/specs/responsive-windowed.md — fits any
// window, phones included; .claude/rules/responsive-layout.md).
//
//   >= 768 px  the fixed 16rem side menu beside the page (unchanged).
//   <  768 px  a top bar (firm name + a 44 px ☰) instead; ☰ opens the same
//              menu as a drawer over the page.  The drawer is a modal dialog
//              (useOverlayMenu: focus moves in, Tab stays in, Esc closes, focus
//              returns to ☰); a click outside, any link in it (also the page
//              already open) and a page change close it.  While closed it is
//              off-screen, `inert` and aria-hidden — hidden by translate, never
//              by `visibility` (reduce motion animates visibility; see Layout).
// The tier comes from the same (min-width: 768px) query Tailwind's `md:`
// uses, so the page column's `md:ml-64` and the drawer agree.
//
// The page column is `flex-1 min-w-0`: a page is never wider than the window,
// so a wide table scrolls inside its own overflow-x-auto box instead of
// pushing the page sideways (before this, the column kept min-width:auto and
// every portal page scrolled sideways on a phone).
export default function PortalLayout() {
  const { client, loading, logout } = usePortalAuth()
  const [firm, setFirm] = useState(null)
  const tier = useViewportTier()
  const isDrawer = tier === 'narrow'
  const [menuOpen, setMenuOpen] = useState(false)
  const overlayOpen = isDrawer && menuOpen
  const location = useLocation()
  const sidebarRef = useRef(null)
  const menuButtonRef = useRef(null)
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  // Note: ResetChunkReloadFlag is rendered inside the inner Suspense so
  // it only fires when the lazy portal page successfully mounts.

  useEffect(() => {
    // Public firm branding via the portal-scoped endpoint (no staff auth required)
    portalApi.get('/api/portal/firm').then(r => setFirm(r.data)).catch(() => {})
  }, [])

  // A page change from anywhere (a link in the page, Back) closes the drawer,
  // and so does the window widening past the drawer size.
  useEffect(() => { setMenuOpen(false) }, [location.pathname])
  useEffect(() => { if (!isDrawer) setMenuOpen(false) }, [isDrawer])

  useOverlayMenu({
    open: overlayOpen,
    containerRef: sidebarRef,
    onClose: closeMenu,
    returnFocusRef: menuButtonRef,
  })

  // Any link chosen in the open drawer closes it — also the page already
  // open, which changes no route.
  const closeOnLinkClick = (e) => {
    const a = e && e.target && typeof e.target.closest === 'function' ? e.target.closest('a[href]') : null
    if (a && sidebarRef.current && sidebarRef.current.contains(a)) setMenuOpen(false)
  }

  // T2-B Phase 1 — idle-prefetch every portal page after mount.  Portal
  // users typically visit 3+ pages per session (Dashboard → Invoices →
  // Documents), so prefetching them all costs little and eliminates
  // per-nav wait.  All 8 portal pages are already lazy in App.js.
  useIdlePrefetch(() => [
    () => import('../pages/portal/PortalDashboard'),
    () => import('../pages/portal/PortalInvoices'),
    () => import('../pages/portal/PortalInvoiceDetail'),
    () => import('../pages/portal/PortalTrust'),
    () => import('../pages/portal/PortalMatters'),
    () => import('../pages/portal/PortalDocuments'),
    () => import('../pages/portal/PortalEvents'),
    () => import('../pages/portal/PortalProfile'),
  ])

  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-gray-100"><p className="text-gray-500">Loading…</p></div>
  }
  if (!client) return <Navigate to="/portal/login" replace />

  const firmName = firm?.firm_name || 'Omnia Practice Management'
  // 44 px tap targets in the drawer (a phone); the side menu keeps its size.
  // In px, not rem: the root font size is 13-18 px (index.css, the text-size
  // preference), so w-11 / h-11 (2.75rem) is only 38.5 px at the default 14.
  const tap = isDrawer ? 'min-h-[44px]' : ''
  const drawerPlacement = isDrawer
    ? `top-0 left-0 z-50 max-w-[85vw] transition-transform duration-200 ${overlayOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`
    : ''

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {overlayOpen && (
        <div
          data-testid="portal-menu-backdrop"
          onClick={closeMenu}
          className="fixed inset-0 z-40 bg-black/40"
          aria-hidden="true"
        />
      )}
      <div
        id="portal-sidebar"
        ref={sidebarRef}
        role={overlayOpen ? 'dialog' : undefined}
        aria-modal={overlayOpen ? 'true' : undefined}
        aria-label={overlayOpen ? 'Portal menu' : undefined}
        aria-hidden={isDrawer && !overlayOpen ? 'true' : undefined}
        inert={isDrawer && !overlayOpen ? true : undefined}
        onClickCapture={overlayOpen ? closeOnLinkClick : undefined}
        className={`w-64 bg-primary-700 text-white flex flex-col fixed h-full ${drawerPlacement}`}
      >
        <div className={`px-6 py-6 border-b border-primary-600 ${isDrawer ? 'flex items-start justify-between gap-2' : ''}`}>
          <div className="min-w-0">
            <h1 className="text-xl font-bold">{firmName}</h1>
            <p className="text-primary-100 text-xs mt-1">Client Portal</p>
          </div>
          {isDrawer && (
            <button
              type="button"
              onClick={closeMenu}
              aria-label="Close menu"
              title="Close menu"
              className="shrink-0 -mr-3 -mt-2 w-[44px] h-[44px] rounded-lg text-primary-100 hover:text-white hover:bg-primary-600 flex items-center justify-center text-xl">
              ✕
            </button>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-1">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tap} ${
                  isActive ? 'bg-primary-600 text-white' : 'text-primary-100 hover:bg-primary-600 hover:text-white'
                }`
              }
            >
              <span>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-primary-600">
          <p className="text-sm font-semibold truncate">{client.name}</p>
          <p className="text-xs text-primary-200 truncate">{client.portal_email || client.email}</p>
          <button
            onClick={logout}
            className={`mt-3 w-full text-left text-sm text-primary-100 hover:text-white transition-colors ${tap}`}
          >
            Sign Out
          </button>
        </div>
      </div>
      <div className="flex-1 min-w-0 md:ml-64">
        {/* Narrow windows: the top bar in place of the side menu. */}
        {isDrawer && (
          <header className="sticky top-0 z-30 bg-primary-700 text-white flex items-center gap-2 px-2 py-1 shadow">
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={overlayOpen}
              aria-controls="portal-sidebar"
              title="Menu"
              className="shrink-0 w-[44px] h-[44px] rounded-lg hover:bg-primary-600 flex items-center justify-center text-xl">
              ☰
            </button>
            <div className="min-w-0 flex-1">
              <p className="font-bold truncate" title={firmName}>{firmName}</p>
              <p className="text-primary-100 text-xs">Client Portal</p>
            </div>
          </header>
        )}
        <main className="p-4 md:p-8">
          {/* T2-B Phase 1 — inner boundary + Suspense so lazy portal
              page navs don't unmount the portal shell. */}
          <ChunkLoadErrorBoundary>
            <Suspense fallback={<PageLoading />}>
              <ResetChunkReloadFlag />
              <Outlet />
            </Suspense>
          </ChunkLoadErrorBoundary>
        </main>
      </div>
    </div>
  )
}
